import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { FacilityNode } from '@/modules/companies';
import { ALL_FACILITIES_QUERY } from '@/modules/companies'
import React, { useCallback, useMemo, useState } from 'react'
import type { DropdownFilter } from '@/types/graphql/enums'
import type { SelectProps as BaseSelectProps} from './Select';
import { Select } from './Select'
import { getNodes } from '@/utils/graphql'

interface FacilitySelectSelectProps
  extends Omit<BaseSelectProps<string, FacilityNode>, 'options'> {
  dropdownPageName?: DropdownFilter
  showInManualTimeEntryOvertime?: boolean
}

export const FacilitySelect: React.FC<FacilitySelectSelectProps> = ({
  dropdownPageName,
  nullable = true,
  ...selectProps
}) => {
  const { facility: placeholder } = useTranslate({
    facility: 'common.facility',
  })

  const [query, setQuery] = useState('')
  const { data, loading } = useQuery(ALL_FACILITIES_QUERY)

  const facilities = getNodes(data?.allFacilities)

  const getLabel = useCallback((facility: FacilityNode) => {
    return <span className="name">{facility.name}</span>
  }, [])

  const options = useMemo<SelectOption<string, FacilityNode>[]>(() => {
    if (!facilities) return []

    const options = facilities
      .filter(facility =>
        facility.name.toLowerCase().includes(query.toLowerCase())
      )
      .map(facility => ({
        value: facility.id,
        label: getLabel(facility),
        extra: facility,
      }))
    return options
  }, [facilities, getLabel, query])

  return (
    <Select
      options={options}
      loading={loading}
      placeholder={placeholder}
      searchable={false}
      filterBackground="white"
      optionHoverBackground="gray10"
      onFilterChange={setQuery}
      nullable={nullable}
      {...selectProps}
    />
  )
}
